import { PlusIcon, QuestionnaireIcon } from "@xola/icons";
import { Dot, Tooltip } from "@xola/ui-kit";
import {
    areAllFormFieldsEmpty,
    countFilledFields,
    getQuestionnaireStatusIconColor,
    isEquipmentItem,
    isMembershipItem,
    isOtherSellerPurchase,
    PurchaseActionOption,
} from "common";
import React, { Suspense, useMemo, useState } from "react";
import { useMutation } from "react-query";
import {
    DigitalWaiver,
    FieldType,
    Form,
    FormResponse,
    Guest,
    Package,
    PartnerPackage,
    Permission,
    Product,
    Purchase,
    PurchaseItem,
} from "types";
import { IsGranted } from "../../Common/IsGranted";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { RefundOption } from "../../Dashboard/RefundOption";
import { Loader } from "../../Loader";
import {
    CollectBalanceOption,
    CollectBalanceOptionsModal,
} from "../../PurchaseModification/CollectBalanceDeciderModal";
import { CollectPurchaseBalanceViaInvoiceModal } from "../../PurchaseModification/CollectBalanceViaInvoiceModal";
import { CollectPurchaseBalanceModal } from "../../PurchaseModification/CollectPurchaseBalanceModal";
import { PurchaseManualRefundModal } from "../../PurchaseModification/PurchaseManualRefundModal";
import {
    PurchaseReturnPaymentModal,
    ReturnPaymentSource,
} from "../../PurchaseModification/Refund/PurchaseReturnPaymentModal";
import { LinkedWaiverPopover } from "../../PurchaseModification/Waiver/LinkedWaiverPopover";
import { getISOFormattedDateString } from "../../Questionnaire/DateOfBirthField";
import { QuestionnaireModal } from "../../Questionnaire/QuestionnaireModal";
import { PurchaseAttendees } from "./PurchaseAttendees";
import { PurchasePayers } from "./PurchasePayers";
import { api } from "@/api/api";
import { PriceStrategy } from "@/components/PurchaseModification/PaymentBreakdown";
import { usePurchaseFormConfig } from "@/hooks/questionnaireFormHooks";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useCollection } from "@/hooks/useCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useResource } from "@/hooks/useResource";
import { useModal } from "@/hooks/useVisibility";
import { getGuestsToSave } from "@/models/Guest";
import { isSharedDemographicsPackage } from "@/models/Package";
import { isPackageItem } from "@/models/Purchase";
import { isProductHasLinkedWaiverTemplate } from "@/models/Waiver";
import { canModifyWaivers, canViewWaivers } from "@/stores/globalStore";
import { getSeller } from "@/utils/getSeller";
import { updateCache } from "@/utils/updateCache";

const CreatePurchaseSplitPaymentModal = React.lazy(async () =>
    import("../../PurchaseCreate/SplitPayment/CreatePurchaseSplitPaymentModal").then((module) => ({
        default: module.CreatePurchaseSplitPaymentModal,
    })),
);

interface SplitPayAttendeesAndPayersProps {
    purchase: Purchase;
    item?: PurchaseItem;
    isAttendeeUnique?: boolean;
}

export const SplitPayAttendeesAndPayers = ({ purchase, item, isAttendeeUnique }: SplitPayAttendeesAndPayersProps) => {
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();
    const seller = getSeller();
    const sellerHasCasEnabled = seller?.preferences?.crossAccountSelling?.enabled ?? false;

    const splitPaymentModal = useModal(false);
    const questionnaireModal = useModal(false);

    const [refundAmount, setRefundAmount] = useState<number | null>(null);
    const [manualRefundAmount, setManualRefundAmount] = useState(0);

    const [selectedOption, setSelectedOption] = useState<PurchaseActionOption | null>(null);
    const [selectedRefundOption, setSelectedRefundOption] = useState<RefundOption | null>(null);
    const [returnPaymentSource, setReturnPaymentSource] = useState<ReturnPaymentSource | null>(null);

    const { data: productItem } = useResource<Product>(["products", item?.product.id]);
    const { data: product } = useResource<Package | PartnerPackage>(["products", item?.parentProduct?.id], null, {
        enabled: isPackageItem(item),
    });
    const { data: waiverTemplates, isLoading: isLoadingWaivers } = useCollection<DigitalWaiver>(
        "waiverTemplates",
        null,
        { enabled: canViewWaivers() },
    );

    const isWaiverLinked = productItem ? isProductHasLinkedWaiverTemplate(waiverTemplates, productItem) : false;
    const isSharedDemographicPackage = isSharedDemographicsPackage(product);

    const { data: purchaseNew, isLoading: isFetchingPurchase } = useResource<Purchase>(
        ["purchases", purchase.id],
        { include: "form", expand: sellerHasCasEnabled && purchase.partnerSeller ? "waiver,partner" : "waiver" },
        { keepPreviousData: true },
    );

    const initialItem = item ?? purchase.items[0];
    const purchaseItem = purchaseNew?.items?.find((itemItr) => itemItr.id === initialItem.id) ?? initialItem;

    const { mutateAsync: updatePurchase, isLoading } = useMutation(async (payload: any) =>
        api.patch(`/purchases/${purchase.id}`, payload),
    );

    const { guests, formResponse } = purchaseItem;
    const { form } = isSharedDemographicPackage && purchaseNew ? purchaseNew : purchaseItem;

    const { hasOrganizerConfigOrResponse } = usePurchaseFormConfig(purchaseItem);

    const itemLevelGuest = useMemo(() => {
        if (!hasOrganizerConfigOrResponse) {
            return null;
        }

        let primaryGuestIndex = guests.findIndex((guest) => guest.primary);
        if (primaryGuestIndex < 0) {
            const guestIndex = guests.findIndex((guest) => guest.traveler?.id === purchase.organizer?.id);
            primaryGuestIndex = Math.max(guestIndex, 0);
        }

        const primaryGuest = guests[primaryGuestIndex];

        // Equipment purchases with "Collect demographic information" off have no attendee guests, but the
        // questionnaire is still collected at the item level (purchaseItem.formResponse). Provide an
        // item-level guest so the questionnaire modal can open — it reads/writes item.formResponse. The name
        // defaults to the purchase's customer name since there is no individual attendee.
        if (!primaryGuest) {
            const customerName = purchaseNew?.customerName ?? purchase.customerName ?? "";
            // formResponse.name can be an empty string, so fall back to the customer name when it is blank.
            const name = formResponse?.name ? formResponse.name : customerName;
            return { name, primary: true, formResponse } as Guest;
        }

        return {
            ...primaryGuest,
            ...(formResponse && { formResponse }),
            name: !isAttendeeUnique ? primaryGuest.name : formResponse?.name ?? "",
        };
    }, [
        hasOrganizerConfigOrResponse,
        guests,
        isAttendeeUnique,
        formResponse,
        purchase.organizer?.id,
        purchase.customerName,
        purchaseNew?.customerName,
    ]);

    if (isFetchingPurchase) {
        return <Loader />;
    }

    const isMist = purchase.items.length > 1;
    const isSplitPayPayerEnabled = purchase.splitPayment.enabled;
    const isDifferentSellerPurchase = isOtherSellerPurchase(purchase, getSeller().id);
    const showWaivers = !isDifferentSellerPurchase && !isMembershipItem(item) && isWaiverLinked && !isLoadingWaivers;

    const isEquipment = isEquipmentItem(item);
    const equipmentHasNoAttendees = isEquipment && (purchaseItem?.guests?.length ?? 0) === 0;
    // Equipment purchases with "Collect demographic information" off have no attendees and are treated as
    // organizer-level (!isAttendeeUnique). Still show the organizer-level "Questionnaire" section (title +
    // questionnaire/waiver buttons) as long as there is a questionnaire form or a linked waiver.
    const hasQuestionnaireOrWaiver = (form?.fields?.length ?? 0) > 0 || showWaivers;
    const showAttendees = equipmentHasNoAttendees
        ? !isAttendeeUnique && hasQuestionnaireOrWaiver
        : isMist
        ? item !== undefined
        : true;
    const showPayers = isMist ? isSplitPayPayerEnabled && item === undefined : isSplitPayPayerEnabled;
    const isPartnerPurchaseWithoutEditPermissions = isDifferentSellerPurchase && !purchase.partner?.editPurchases;

    const handleQuestionnaireSubmit = async (
        updatedGuest: Guest,
        priceStrategy?: PriceStrategy,
        nextOption?: PurchaseActionOption.COLLECT_BALANCE_OPTIONS | RefundOption.RETURN_PAYMENT,
        refundAmount?: number,
    ) => {
        let updatedGuestFormResponse: FormResponse | undefined;
        const updatedFields =
            updatedGuest.formResponse?.fields?.map((field) => {
                if (field.type === FieldType.DATE_OF_BIRTH) {
                    return { ...field, value: getISOFormattedDateString(field.value) };
                }

                return field;
            }) ?? [];

        if (updatedGuest.formResponse?.fields) {
            updatedGuestFormResponse = { ...updatedGuest.formResponse, fields: updatedFields };
        }

        // If OrganizerOnly form then there will be only one guest and item level questionnaire
        if (hasOrganizerConfigOrResponse) {
            const { formResponse, name } = updatedGuest;
            const { formResponse: purchaseItemFormResponse } = purchaseItem;
            const allUpdatedFields = updatedGuestFormResponse?.fields ?? formResponse?.fields ?? [];
            // Preserve existing photo paths — photos come from the precheck app and are
            // not editable via this modal, so strip any empty photo fields from Formik state
            // and keep the stored paths from the item-level response.
            const nonPhotoFields = allUpdatedFields.filter((f) => f.type !== FieldType.PHOTO);
            const existingPhotoFields =
                purchaseItemFormResponse?.fields?.filter((f) => f.type === FieldType.PHOTO) ?? [];
            await updatePurchase({
                items: [
                    {
                        id: purchaseItem.id,
                        formResponse: {
                            ...purchaseItemFormResponse,
                            name,
                            fields: [...nonPhotoFields, ...existingPhotoFields],
                        },
                        ...(priceStrategy && { _priceStrategy: priceStrategy }),
                    },
                ],
            });
        } else {
            const savedGuest = {
                ...updatedGuest,
                formResponse: updatedGuestFormResponse ?? updatedGuest.formResponse,
            } as Guest;
            const guests = getGuestsToSave(savedGuest, purchaseItem.guests, ["checkInCount", "ticketCode"]);

            await updatePurchase({
                items: [{ id: purchaseItem.id, guests, ...(priceStrategy && { _priceStrategy: priceStrategy }) }],
            });
        }

        invalidate("purchases", purchase.id);

        if (nextOption === PurchaseActionOption.COLLECT_BALANCE_OPTIONS) {
            setSelectedOption(nextOption);
        }

        if (nextOption === RefundOption.RETURN_PAYMENT) {
            setSelectedRefundOption(nextOption);
            setRefundAmount(refundAmount ?? null);
        }
    };

    const resetSelectedRefundOption = () => {
        setReturnPaymentSource(null);
        setSelectedRefundOption(null);
        invalidate("purchases", purchase.id);
    };

    const resetSelectedOption = () => setSelectedOption(null);

    const handleFlushPurchaseCache = () => {
        invalidate("timeslots");
        if (purchaseItem.event) {
            invalidate("events", purchaseItem.event.id);
        }

        updateCache("purchases", purchase);
    };

    const handleOptionsChange = (
        initModalOption: PurchaseActionOption | null,
        initRefundOption: RefundOption | null,
        initRefundAmount: number,
    ) => {
        setSelectedOption(initModalOption);
        setSelectedRefundOption(initRefundOption);
        setReturnPaymentSource(
            initRefundOption === RefundOption.RETURN_PAYMENT ? ReturnPaymentSource.CHANGE_GUEST_QUANTITY : null,
        );
        setRefundAmount(initRefundAmount);
    };

    return (
        <>
            {showAttendees && (
                <div className="space-y-4" data-testid="split-pay-attendees-and-payers-attendees">
                    <div className="flex justify-between">
                        <div className="text-lg font-bold">
                            {!isAttendeeUnique ? " Questionnaire" : isMembershipItem(item) ? "Members" : "Attendees"}
                        </div>

                        <div className="flex items-center space-x-2">
                            {form ? (
                                <div className="flex text-sm">
                                    {!isPartnerPurchaseWithoutEditPermissions && form.fields.length > 0 && (
                                        <SummaryAttendeesBlock
                                            purchaseItem={purchaseItem}
                                            isAttendeeUnique={isAttendeeUnique}
                                            form={form}
                                            onClick={questionnaireModal.show}
                                        />
                                    )}

                                    {itemLevelGuest && (
                                        <QuestionnaireModal
                                            disableRequiredFields
                                            isItemLevel
                                            purchase={purchase}
                                            item={purchaseItem}
                                            isOpen={questionnaireModal.isVisible}
                                            form={form}
                                            guest={itemLevelGuest}
                                            isSaving={isLoading}
                                            onClose={questionnaireModal.hide}
                                            onSubmit={handleQuestionnaireSubmit}
                                        />
                                    )}
                                </div>
                            ) : null}

                            {showWaivers && canModifyWaivers() && (
                                <LinkedWaiverPopover guests={guests} purchaseItem={purchaseItem} />
                            )}
                        </div>
                    </div>

                    {isAttendeeUnique && (
                        <PurchaseAttendees
                            purchaseItem={purchaseItem}
                            form={form}
                            purchase={purchase}
                            onOptionsChange={handleOptionsChange}
                        />
                    )}
                </div>
            )}

            {showPayers && isGranted(Permission.MODIFY_BOOKING_LITE) && (
                <div className="space-y-4" data-testid="split-pay-attendees-and-payers-payers">
                    <div className="flex justify-between">
                        <div className="text-lg font-bold">Payers</div>
                        {purchase.balance > 0 && (
                            <SecondaryButton size="tiny" onClick={splitPaymentModal.show}>
                                <PlusIcon />
                                <span className="pl-1 text-sm font-bold">Split Pay</span>
                            </SecondaryButton>
                        )}
                    </div>
                    <PurchasePayers purchase={purchase} purchaseItem={purchaseItem} />
                </div>
            )}

            <PurchaseReturnPaymentModal
                purchase={purchase}
                item={purchaseItem}
                refundAmount={refundAmount === null ? null : Math.abs(refundAmount)}
                returnPaymentSource={returnPaymentSource}
                manualRefund={(amount: number) => setManualRefundAmount(amount)}
                shouldReduce={false}
                isOpen={selectedRefundOption === RefundOption.RETURN_PAYMENT}
                refundOption={selectedRefundOption}
                onClose={() => {
                    setRefundAmount(null);
                    resetSelectedRefundOption();
                    resetSelectedOption();
                }}
            />

            <PurchaseManualRefundModal
                isOpen={manualRefundAmount > 0}
                amountToRefund={manualRefundAmount}
                purchase={purchase}
                onClose={() => setManualRefundAmount(0)}
            />

            <CollectPurchaseBalanceModal
                purchase={purchase}
                isOpen={selectedOption === PurchaseActionOption.COLLECT_BALANCE}
                onClose={() => {
                    handleFlushPurchaseCache();
                    resetSelectedOption();
                }}
            />

            <CollectBalanceOptionsModal
                isOpen={selectedOption === PurchaseActionOption.COLLECT_BALANCE_OPTIONS}
                purchase={purchase}
                onClose={resetSelectedOption}
                onConfirm={(action: CollectBalanceOption) => {
                    if (action === CollectBalanceOption.COLLECT_NOW) {
                        setSelectedOption(PurchaseActionOption.COLLECT_BALANCE);
                    } else if (action === CollectBalanceOption.COLLECT_VIA_INVOICE) {
                        setSelectedOption(PurchaseActionOption.COLLECT_BALANCE_VIA_INVOICE);
                    } else {
                        resetSelectedOption();
                    }
                }}
            />

            <CollectPurchaseBalanceViaInvoiceModal
                purchase={purchase}
                item={purchaseItem}
                isOpen={selectedOption === PurchaseActionOption.COLLECT_BALANCE_VIA_INVOICE}
                onClose={resetSelectedOption}
            />

            <Suspense fallback={<Loader />}>
                <CreatePurchaseSplitPaymentModal
                    isOpen={splitPaymentModal.isVisible}
                    purchase={purchase}
                    onClose={splitPaymentModal.hide}
                />
            </Suspense>
        </>
    );
};

interface AttendeesSummary {
    purchaseItem: PurchaseItem;
    form: Form;
}

interface SummaryAttendeesBlockProps extends AttendeesSummary {
    onClick: () => void;
    isAttendeeUnique?: boolean;
}

const SummaryAttendeesBlock = ({ purchaseItem, form, isAttendeeUnique, onClick }: SummaryAttendeesBlockProps) => {
    const { isItemLevelFormAndConfig, hasGuestConfigOrResponse, hasOrganizerConfigOrResponse } =
        usePurchaseFormConfig(purchaseItem);

    return (
        <IsGranted permissions={Permission.MODIFY_GUEST_VIEW}>
            {isItemLevelFormAndConfig ? (
                // This block will be displayed for item level questionnaire.
                <Tooltip content="Questionnaire">
                    <SecondaryButton size="small" onClick={onClick}>
                        <SummaryButton
                            isItemLevelForm
                            isAttendeeUnique={isAttendeeUnique}
                            purchaseItem={purchaseItem}
                            form={form}
                        />
                    </SecondaryButton>
                </Tooltip>
            ) : hasGuestConfigOrResponse && hasOrganizerConfigOrResponse ? (
                // This block will be displayed if config was changed from person to item level or vice versa,
                // and there is a response already.
                <div className="flex items-center space-x-3">
                    <SecondaryButton size="small" onClick={onClick}>
                        <SummaryButton
                            isItemLevelForm
                            isAttendeeUnique={isAttendeeUnique}
                            purchaseItem={purchaseItem}
                            form={form}
                        />
                    </SecondaryButton>

                    {isAttendeeUnique && (
                        <SummaryButton isItemLevelForm={false} purchaseItem={purchaseItem} form={form} />
                    )}
                </div>
            ) : (
                // This block will be displayed for guest level questionnaire.
                <Tooltip content="Questionnaire">
                    <SummaryButton
                        isAttendeeUnique={isAttendeeUnique}
                        isItemLevelForm={false}
                        purchaseItem={purchaseItem}
                        form={form}
                    />
                </Tooltip>
            )}
        </IsGranted>
    );
};

interface SummaryButtonProps extends AttendeesSummary {
    isItemLevelForm: boolean;
    isAttendeeUnique?: boolean;
}

const SummaryButton = ({ purchaseItem, form, isItemLevelForm, isAttendeeUnique = true }: SummaryButtonProps) => {
    const { formResponse, guests } = purchaseItem;

    const hasAttendeeNameEntry = !isAttendeeUnique && guests.length > 0;
    const filledFieldCount = countFilledFields(formResponse) + (hasAttendeeNameEntry ? 1 : 0);
    const showAnswerCount = isItemLevelForm && !isAttendeeUnique;
    const showItemLevelRatio = isItemLevelForm && isAttendeeUnique;
    const filled = formResponse && !areAllFormFieldsEmpty(formResponse);

    const dotColor = useMemo(() => {
        if (!form) {
            return "success";
        }

        if (isItemLevelForm) {
            // Equipment items have no attendee guests, so fall back to the organizer as the primary guest.
            const primaryGuest = guests?.[0] ?? (purchaseItem.organizer as Guest | undefined);

            return getQuestionnaireStatusIconColor(
                form,
                {
                    ...primaryGuest,
                    formResponse: {
                        ...purchaseItem.formResponse,
                        fields: [
                            ...(purchaseItem.formResponse?.fields ?? []),
                            ...(primaryGuest?.formResponse?.fields ?? []),
                        ],
                    },
                } as Guest,
                true,
            );
        }

        if (!guests) {
            return "success";
        }

        if (guests.some((guest) => getQuestionnaireStatusIconColor(form, guest) === "danger")) {
            return "danger";
        }

        if (guests.some((guest) => getQuestionnaireStatusIconColor(form, guest) === "warning")) {
            return "warning";
        }

        return "success";
    }, [form, guests, isItemLevelForm, purchaseItem.formResponse, purchaseItem.organizer]);

    // noAdditionalQuestionsInForm var means that in Questionnaire settings we didn't add any specific questions and we have only default question about guest name
    // Guest name not included in formResponse, it goes to guest.name, so we can count that guest filled all questions in that case
    const noAdditionalQuestionsInForm = purchaseItem.form?.fields?.length === 0;
    const guestsData = guests.filter((guest: Guest) => {
        return (
            (guest.formResponse && !areAllFormFieldsEmpty(guest.formResponse)) ||
            (noAdditionalQuestionsInForm && guest.name)
        );
    });

    let displayText = `${guestsData.length}/${guests.length}`;

    if (showAnswerCount) {
        displayText = `${filledFieldCount} ${filledFieldCount === 1 ? "answer" : "answers"}`;
    } else if (showItemLevelRatio) {
        displayText = filled ? "1/1" : "0/1";
    }

    return (
        <div className="flex items-center">
            <span className="flex items-center">
                <Dot className="mr-1" color={dotColor} />
                <QuestionnaireIcon className="mt-0.5" />
            </span>
            <NoTranslate
                as="div"
                className="pl-2 font-bold"
                data-testid="split-pay-attendees-and-payers-questionnaires-count"
            >
                {displayText}
            </NoTranslate>
        </div>
    );
};
